'use client';

import { ElementType, useEffect, useMemo, useRef, useState } from 'react';

import {
    Alignable,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Icon,
    IconName,
    IconSize,
    Input,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';

import {
    EligibilityError,
    FastPass,
    HTMLWetoElement,
    IWetoSearchProps,
    WetoEndEvent,
    WetoEvent,
    WetoStepChangeEvent,
} from '@app-types/eligibility';
import { UserType } from '@app-types/user';

import { API_X_SOURCE } from '@constants/api';
import {
    ELIGIBILITY_ERROR_STATUS,
    ELIGIBILITY_STEP_NAME,
    ELIGIBILITY_STEP_STATES,
    ELIGIBILITY_STEP_STATUS,
    TYPE_ELIGIBILITY,
} from '@constants/eligibility';

import { EligibilityAddress, JourneyConfigType, sendEligibilityTracking } from '@eligibility/helpers';

interface EligibilityWidgetProps {
    type: TYPE_ELIGIBILITY;
    fastPass?: FastPass;
    config: JourneyConfigType;
    address?: EligibilityAddress;
    isMapFirstStep: boolean;
    isFirstStep: boolean;
    currentStepStatus: ELIGIBILITY_STEP_STATUS;
    currentStep: ELIGIBILITY_STEP_NAME;
    error?: EligibilityError;

    handleStepStatusChange?: (status: ELIGIBILITY_STEP_STATUS) => void;
    handleStepChange?: (event: WetoStepChangeEvent['event']) => void;
    handleError?: (error?: EligibilityError) => void;
    handleEnd?: (event: WetoEndEvent['event']) => Promise<void>;

    user?: UserType;
}

const appConfig = getAppConfig();

export function EligibilityWidget({
    type,
    config,
    fastPass,
    handleStepStatusChange,
    handleStepChange,
    handleError,
    handleEnd,
    address,
    isMapFirstStep,
    isFirstStep,
    currentStepStatus,
    currentStep,
    error,
    user,
}: Readonly<EligibilityWidgetProps>) {
    const weto = useRef<HTMLWetoElement>(null);
    const [isRendered, setIsRendered] = useState<boolean>(false);
    const [hasStartedTyping, setHasStartedTyping] = useState<boolean>(false);

    const widget = useMemo(() => {
        switch (true) {
            case fastPass !== undefined:
                return 'weto-funnel';
            case type === TYPE_ELIGIBILITY.SEARCH && !address && !isMapFirstStep:
                return 'weto-linear-address';
            case type === TYPE_ELIGIBILITY.PTO:
                return 'weto-elig-pto';
            case type === TYPE_ELIGIBILITY.SEARCH && isMapFirstStep:
                return 'weto-map';
        }
    }, [type, address, isMapFirstStep, fastPass]);

    const previousStep = () => {
        weto.current?.back();
    };

    const nextStep = () => {
        weto.current?.next();
    };

    const reset = () => {
        weto.current?.reset();
    };

    const handleEvent = async ({ event, type: eventType }: WetoEvent) => {
        handleError?.(undefined);
        if (eventType === ELIGIBILITY_STEP_STATES.STEP_STATUS) {
            if (event.status === ELIGIBILITY_STEP_STATUS.RESET) {
                setHasStartedTyping(false);
                reset();
            } else {
                setHasStartedTyping(true);
                handleStepStatusChange?.(event.status);
            }
        } else if (eventType === ELIGIBILITY_STEP_STATES.STEP_CHANGE) {
            handleStepChange?.(event);
        } else if (eventType === ELIGIBILITY_STEP_STATES.ERROR) {
            handleError?.(event);
        } else if (eventType === ELIGIBILITY_STEP_STATES.TRACKING) {
            sendEligibilityTracking(config, event);
        } else if (eventType === ELIGIBILITY_STEP_STATES.END) {
            handleEnd?.(event);
        }
    };

    const shouldShowNext = useMemo(() => {
        const noNextSteps: ELIGIBILITY_STEP_NAME[] = [
            ELIGIBILITY_STEP_NAME.GMAP,
            ELIGIBILITY_STEP_NAME.NAME,
            ELIGIBILITY_STEP_NAME.HOUSING,
            ELIGIBILITY_STEP_NAME.HOUSING_CONFIRM,
            ELIGIBILITY_STEP_NAME.LOADING_SEARCH,
        ];
        return (
            !noNextSteps.includes(currentStep) &&
            !isFirstStep &&
            currentStepStatus !== ELIGIBILITY_STEP_STATUS.LOADING &&
            error?.type !== ELIGIBILITY_ERROR_STATUS.TECHNICAL
        );
    }, [currentStep, isFirstStep, currentStepStatus, type]);

    const shouldShowBackButton = useMemo(() => {
        const noBackLinkSteps: string[] = [
            ELIGIBILITY_STEP_NAME.SINGLE_SEARCH_ELIG,
            ELIGIBILITY_STEP_NAME.PTO_ELIG,
            ELIGIBILITY_STEP_NAME.LOADING_SEARCH,
            ELIGIBILITY_STEP_NAME.GMAP,
            ELIGIBILITY_STEP_NAME.LINEAR_ADDRESS_CARTO,
        ];
        return (
            !isFirstStep &&
            !noBackLinkSteps.includes(currentStep) &&
            currentStepStatus !== ELIGIBILITY_STEP_STATUS.LOADING &&
            error?.type !== ELIGIBILITY_ERROR_STATUS.TECHNICAL &&
            type !== TYPE_ELIGIBILITY.FUNNEL
        );
    }, [isFirstStep, currentStep, currentStepStatus, error, type]);

    useEffect(() => {
        weto.current?.render(
            {
                disableStep: ['pto'],
                hiddenController: true,
                api: {
                    baseUrl: appConfig.b2r.url, // Generic front api url not sapi only
                    token: user?.accessToken,
                    xSource: API_X_SOURCE,
                },
            },
            address ?? null,
            fastPass,
            true,
        );
        weto.current?.onEvent(handleEvent);
        setIsRendered(true);
    }, [address, user, fastPass, widget]);

    const Weto = widget as ElementType<IWetoSearchProps>;

    if (fastPass) {
        return <Weto ref={weto} />;
    }

    return (
        <>
            <div hidden={!shouldShowBackButton} className='has-text-left'>
                <Button className='link' onClick={previousStep}>
                    <Icon name={IconName.ARROW_LEFT} size={IconSize.SMALLER} />
                    Retour
                </Button>
            </div>
            <Columns
                align={Alignable.ALIGNED_CENTER}
                verticalAlign={Alignable.ALIGNED_CENTER}
                marginless
                className='is-justified-center'
            >
                <Column>
                    {!isRendered && (
                        <Input
                            disabled
                            loading
                            className='is-marginless'
                            placeholder='Ex : 33 rue de Rivoli, 75004 Paris'
                        />
                    )}
                    <Weto ref={weto} manual-render />
                </Column>
                {isFirstStep && (
                    <Column narrow>
                        <Button
                            variant={ButtonVariant.CONVERSION}
                            loading={currentStepStatus === ELIGIBILITY_STEP_STATUS.LOADING}
                            disabled={currentStepStatus !== ELIGIBILITY_STEP_STATUS.VALID}
                            onClick={hasStartedTyping ? nextStep : undefined}
                            data-cy='run-eligibility'
                        >
                            Tester mon éligibilité
                        </Button>
                    </Column>
                )}
            </Columns>
            {shouldShowNext && (
                <Columns align={Alignable.ALIGNED_CENTER}>
                    <Column>
                        <Button
                            variant={ButtonVariant.CONVERSION}
                            onClick={nextStep}
                            disabled={currentStepStatus === ELIGIBILITY_STEP_STATUS.INVALID}
                        >
                            Vérifier mon adresse
                        </Button>
                    </Column>
                </Columns>
            )}
        </>
    );
}
